// Sport Workspaces v1.1 — the PRESENTATION-layer registry for the hero sport
// switcher. A "navigable workspace" is purely a UI concept: which workspaces
// have their own dashboard + sidebar in the app right now.
//
// This is deliberately SEPARATE from sportsRegistry.ts. That registry is the
// slip-gating / calibration source of truth (only 'football' is slip-live;
// Tennis is an honest 'planned' pilot there). Tennis HAS a navigable workspace
// (its pilot UI) but that says nothing about whether it may generate slips —
// that stays governed by the coverage/calibration gate. Never conflate the two.
//
// v1.1 — adds 'international' as a fourth workspace sibling. It is NOT a new
// sport (football is still the underlying sport) — it's a context workspace
// for the international football surface (UCL/UEL/Euro qual, WC 2026, futures,
// LADDER-Intl, International Acca). Modelled as a peer key here so the hero
// switcher treats it as a 4th sibling pill; engine/substrate stay shared.
//
// Workspaces without a UI yet are simply absent here. Appending a new one
// = one entry below.

export type SportWorkspaceKey = "football" | "tennis" | "basketball" | "international";

export interface SportWorkspace {
  readonly key: SportWorkspaceKey;
  readonly name: string;
  readonly icon: string;
  /** Landing route when the user switches to this workspace. */
  readonly home: string;
  /** Path prefixes this workspace owns — used to infer the active workspace
   *  from the URL. Football is the fallback (no prefixes) so it owns everything
   *  not claimed by a more specific workspace. */
  readonly matchPrefixes: readonly string[];
}

export const SPORT_WORKSPACES: readonly SportWorkspace[] = [
  { key: "football", name: "Football", icon: "football", home: "/home", matchPrefixes: [] },
  { key: "tennis", name: "Tennis", icon: "tennis", home: "/exchange/tennis/signals", matchPrefixes: ["/exchange/tennis"] },
  // Basketball has a navigable workspace (its dormant pilot dashboard) but is
  // NOT slip-live — that stays governed by sportsRegistry.ts ('planned') + the
  // server-side coverage/calibration gate. Presence here is presentation only.
  { key: "basketball", name: "Basketball", icon: "basketball", home: "/exchange/basketball", matchPrefixes: ["/exchange/basketball"] },
  // International — context workspace (NOT a sport). Underlying sport stays
  // 'football'; this is the navigation surface for the international football
  // world (UCL/UEL/Euro/WC/intl friendlies + LADDER-Intl + International Acca).
  // Engine, substrate and slip-moderator are all shared with the football
  // workspace — only navigation and presentation are split here.
  // Claims both /exchange/international/* (the workspace landing + new screens)
  // AND /intl-acca/* (the existing Acca/Hub/Ladder/Calendar/Pass route tree),
  // so deep-linking into the established Acca pages keeps the user inside the
  // International workspace and sidebar.
  { key: "international", name: "International", icon: "international", home: "/exchange/international", matchPrefixes: ["/exchange/international", "/intl-acca"] },
] as const;

export const DEFAULT_SPORT: SportWorkspaceKey = "football";

const BY_KEY: ReadonlyMap<string, SportWorkspace> = new Map(
  SPORT_WORKSPACES.map((s) => [s.key, s]),
);

export function workspaceByKey(key: string | null | undefined): SportWorkspace {
  return (key && BY_KEY.get(key)) || BY_KEY.get(DEFAULT_SPORT)!;
}

// The active sport is whichever workspace's prefix the path matches; Football
// is the fallback for every non-prefixed route (Home, Exchange, Tools, etc).
export function activeWorkspaceForPath(path: string): SportWorkspace {
  const match = SPORT_WORKSPACES.find(
    (s) =>
      s.matchPrefixes.length > 0 &&
      s.matchPrefixes.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p)),
  );
  return match ?? workspaceByKey(DEFAULT_SPORT);
}
